import { useTranslations } from '@vocab/react';
import { IconSearch, Box, TextField, Bleed } from 'braid-design-system';
import { observer } from 'mobx-react';
import {
  type ChangeEvent,
  type FC,
  type FormEvent,
  useEffect,
  useState,
} from 'react';

import translations from './.vocab';

import * as styles from './FilterSearchBar.css';

interface Props {
  lastResultSearchText: string;
  value: string;
  onChange: (text: string) => void;
  onSearch: () => void;
}

export const FilterSearchBar: FC<Props> = observer(
  ({ value, lastResultSearchText, onSearch, onChange }) => {
    const { t } = useTranslations(translations);

    const [isSearchValid, setIsSearchValid] = useState(true);

    const resetFormValidation = () => {
      setIsSearchValid(true);
    };

    useEffect(() => {
      if (lastResultSearchText === '') {
        // If submitting an empty search result disable the input
        resetFormValidation();
      }
    }, [lastResultSearchText]);

    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      resetFormValidation();
      onChange(event.target.value);
    };

    const onBlur = () => {
      if (value === '') {
        resetFormValidation();
      }
    };

    const onSearchSubmit = () => {
      if (value === '') {
        onSearch();
      }

      if (value.length < 3) {
        setIsSearchValid(false);
        return;
      }

      onSearch();
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onSearchSubmit();
    };

    const onClear = () => {
      onChange('');
      resetFormValidation();
      onSearchSubmit();
    };

    return (
      <Box
        width="full"
        component="form"
        onSubmit={onSubmit}
        data-testid="searchbar"
        className={styles.wrapperStyle}
      >
        <TextField
          id="search"
          aria-label={t('Search names, role titles and companies')}
          type="search"
          value={value}
          onChange={onInputChange}
          onClear={onClear}
          placeholder={t('Search names, role titles and companies')}
          autoComplete="off"
          onBlur={onBlur}
          icon={
            // This is because we are forcing Braid TextField to have small font size
            // FIXME: NVL - check the bleed value are correct
            <Bleed top={['none', 'xxsmall']}>
              <IconSearch tone="neutral" />
            </Bleed>
          }
          message={isSearchValid ? '' : t('Must be at least 3 characters')}
          tone={isSearchValid ? 'neutral' : 'critical'}
        />
      </Box>
    );
  },
);
